import { isAxiosError } from 'axios'
import { useEffect, useRef } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import {
  productRecommendationListResponseSchema,
  productRecommendationMutationResponseSchema,
  productRecommendationAcceptResponseSchema,
  type ProductRecommendationConfidence,
  type ProductRecommendationReason,
  type ProductRecommendationResponse,
  type ProductRecommendationStatus,
  type UpdateMetadataFieldProposal,
} from '@artem-lex/common-server/productRecommendations/schemas'
import {
  PRODUCT_LAW_KEYS,
  type ProductLawKey,
} from '@artem-lex/common-server/productApplicability/schemas'
import i18n from 'i18next'
import { toast } from 'sonner'
import { apiClient } from '@/shared/api/apiClient'
import { OPEN_ROHS_PARTS_QUERY_KEY } from '@/shared/hooks/api/openRohsParts'
import { PRODUCT_MACHINERY_COMPLIANCE_QUERY_KEY } from '@/shared/hooks/api/machineryCompliance'
import type { SupportedLanguage } from '@/shared/config/languages'
import {
  llmChatSessionQueryKey,
  llmChatSessionsListQueryKey,
} from '@/shared/hooks/api/llmChat'
import {
  type LatestWorkflowForEntityResponse,
  getLatestWorkflowForEntity,
  type WorkflowEvent,
} from '@/shared/hooks/api/workflows'
import { consumeWorkflowEventStream } from '@/shared/hooks/api/consumeWorkflowEventStream'
import type { MachineryDirectiveCompliance } from './machineryDirectiveCompliance'

export const PRODUCTS_QUERY_KEY = 'products' as const

export const V2_PRODUCTS_QUERY_KEY = 'v2Products' as const

export const PRODUCT_INIT_WORKFLOW_TYPE = 'product_init' as const

export const PRODUCT_METADATA_REFRESH_WORKFLOW_TYPE =
  'product_metadata_refresh' as const

export const PRODUCT_APPLICABILITY_DISCOVER_WORKFLOW_TYPE =
  'product_applicability_discover' as const

const PRODUCT_DISCOVER_WORKFLOW_POLL_MS = 2500

type ProductMetadata = {
  name: string | null
  description: string | null
  yearOfManufacture: number | null
  productModel: string | null
  sku?: string | null
}

export interface ProductInferredFact {
  id: string
  label: string
  value: string
  confidence?: 'low' | 'medium' | 'high'
  rationale?: string
  updatedAt: string
}

export interface ProductInferredFactsDocument {
  items: ProductInferredFact[]
}

export type ProductMetadataFieldKey = keyof ProductMetadata

export type ProductRecommendationKind = ProductRecommendationResponse['kind']

export type { ProductRecommendationStatus }
export type { ProductRecommendationReason }
export type { ProductRecommendationConfidence }

export type ProductRecommendationField = UpdateMetadataFieldProposal

export type ProductRecommendation = ProductRecommendationResponse

export type LexProduct = ProductMetadata & {
  id: string
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
  /** After first successful `product_init`; server guard for re-fill. */
  isInitialized: boolean
  inferredFactsJson: ProductInferredFactsDocument | null
  organizationId: string
  userId: string | null
}

export type V2ProductListItem = {
  id: string
  name: string | null
  sku: string | null
  machinery: ProductLawApplicability | null
  rohs: ProductLawApplicability | null
  emc: ProductLawApplicability | null
  lvd: ProductLawApplicability | null
  red: ProductLawApplicability | null
  weee: ProductLawApplicability | null
}

/** All fields optional so the API can create a minimal placeholder (e.g. upload-first flow). */
export type CreateLexProductPayload = {
  name?: string | null
  description?: string | null
  associatedPartyId?: string | null
  yearOfManufacture?: number | null
  productModel?: string | null
}

export type CreateV2ProductPayload = {
  name: string
  sku: string
  productModel?: string
}

export type ProductLawApplicabilityStatus =
  | 'UNDECIDED'
  | 'NEEDS_GRILLING'
  | 'APPLICABLE'
  | 'NOT_APPLICABLE'

export type ProductLawApplicabilitySource = 'HEURISTIC' | 'USER_FORCE'

export type ProductLawApplicability = {
  status: ProductLawApplicabilityStatus
  answers: Record<string, unknown> | null
  version: number | null
  source: ProductLawApplicabilitySource | null
  forceReason: string | null
  runState?: 'idle' | 'running'
  grillingSummary?: string | null
}

export { PRODUCT_LAW_KEYS }
export type { ProductLawKey }

export type SetProductLawApplicabilityPayload = {
  law: ProductLawKey
  applicability: ProductLawApplicability
}

export type ProductApplicabilityState = {
  machinery: ProductLawApplicability | null
  rohs: ProductLawApplicability | null
  emc: ProductLawApplicability | null
  lvd: ProductLawApplicability | null
  red: ProductLawApplicability | null
  weee: ProductLawApplicability | null
  fgas: ProductLawApplicability | null
  ped: ProductLawApplicability | null
  cra: ProductLawApplicability | null
}

export type LawQuestionnaireQuestionGroup =
  | 'inScope'
  | 'transitional'
  | 'categories'
  | 'exclusions'

export type LawQuestionnaireCategoryRule = 'any' | 'exactlyOne' | null

export type LawQuestionnaireQuestion = {
  questionKey: string
  group: LawQuestionnaireQuestionGroup
  label: string
  helper: string | null
  answer: boolean | null
  rationale: string | null
  hasGap: boolean
}

export type LawQuestionnaireView = {
  law: ProductLawKey
  version: number
  categoryRule: LawQuestionnaireCategoryRule
  requiresCompletePackBeforeVerdict: boolean
  questions: LawQuestionnaireQuestion[]
  applicability: ProductLawApplicability | null
}

export type SetProductLawQuestionnaireAnswersPayload = {
  answers: Record<string, boolean | null | string>
}

export type UpdateLexProductPayload = {
  productId: string
  name?: string | null
  description?: string | null
  yearOfManufacture?: number | null
  productModel?: string | null
}

export type DeleteLexProductResponse = {
  message: string
}

export type UpdateProductInferredFactPayload = {
  productId: string
  factId: string
  label: string
  value: string
}

export type DeleteProductInferredFactPayload = {
  productId: string
  factId: string
}

export type GetProductRecommendationsPayload = {
  productId: string
  sessionId: string
  statuses?: ProductRecommendationStatus[]
}

export type ResolveProductRecommendationPayload = {
  productId: string
  recommendationId: string
}

export function useListProducts() {
  return useQuery<LexProduct[], Error>({
    queryKey: [PRODUCTS_QUERY_KEY],
    queryFn: async () => {
      const response = await apiClient.get<LexProduct[]>('/products')
      return response.data
    },
  })
}

export function useListV2Products() {
  return useQuery<V2ProductListItem[], Error>({
    queryKey: [V2_PRODUCTS_QUERY_KEY, 'list'],
    queryFn: async () => {
      const response = await apiClient.get<{ products: V2ProductListItem[] }>(
        '/v2/products'
      )
      return response.data.products
    },
  })
}

export function useCreateProduct() {
  const queryClient = useQueryClient()

  return useMutation<LexProduct, Error, CreateLexProductPayload>({
    mutationFn: async (payload: CreateLexProductPayload) => {
      const body = Object.fromEntries(
        Object.entries(payload).filter(([, value]) => value !== undefined)
      ) as Record<string, unknown>
      const response = await apiClient.post<LexProduct>('/products', body)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
    },
  })
}

export function useCreateV2Product() {
  const queryClient = useQueryClient()

  return useMutation<LexProduct, Error, CreateV2ProductPayload>({
    mutationFn: async (payload: CreateV2ProductPayload) => {
      const body: { name: string; sku: string; productModel?: string } = {
        name: payload.name,
        sku: payload.sku,
      }
      const trimmedModel = payload.productModel?.trim()
      if (trimmedModel != null && trimmedModel.length > 0) {
        body.productModel = trimmedModel
      }
      const response = await apiClient.post<LexProduct>('/v2/products', body)
      return response.data
    },
    onSuccess: (product) => {
      void queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
      void queryClient.invalidateQueries({ queryKey: [V2_PRODUCTS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, product.id],
      })
    },
  })
}

export function useGetProductApplicability(productId: string | undefined) {
  return useQuery<ProductApplicabilityState, Error>({
    queryKey: [PRODUCTS_QUERY_KEY, productId, 'applicability'],
    queryFn: async () => {
      const response = await apiClient.get<ProductApplicabilityState>(
        `/products/${productId}/applicability`
      )
      return response.data
    },
    enabled: productId != null && productId.length > 0,
  })
}

function discoverStatusFromEvents(
  events: WorkflowEvent[],
  fallback: string
): string {
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const eventType = events[index]?.type
    if (eventType === 'workflow.completed') {
      return 'completed'
    }
    if (eventType === 'workflow.failed') {
      return 'failed'
    }
    if (eventType === 'workflow.cancelled') {
      return 'cancelled'
    }
  }
  return fallback
}

function workflowResultFromEvents(
  events: WorkflowEvent[],
  fallback: unknown
): unknown {
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index]
    if (event?.type === 'workflow.completed' && 'result' in event) {
      return event.result
    }
  }
  return fallback
}

export type ApplicabilityPackSummary = {
  law: ProductLawKey
  questions: Array<{ questionKey: string; label: string }>
}

export function useApplicabilityPacks() {
  return useQuery<{ packs: ApplicabilityPackSummary[] }, Error>({
    queryKey: [V2_PRODUCTS_QUERY_KEY, 'applicability-packs'],
    queryFn: async () => {
      const response = await apiClient.get<{ packs: ApplicabilityPackSummary[] }>(
        '/v2/products/applicability-packs'
      )
      return response.data
    },
    staleTime: 60 * 60 * 1000,
  })
}

export function useProductDiscoverWorkflowLatest(
  productId: string | undefined,
  enabled = true
) {
  const queryClient = useQueryClient()
  const streamingRunIdRef = useRef<string | null>(null)

  const query = useQuery<LatestWorkflowForEntityResponse, Error>({
    queryKey: [
      PRODUCTS_QUERY_KEY,
      productId,
      'discover-workflow-latest',
    ],
    queryFn: async () =>
      getLatestWorkflowForEntity(
        'product',
        productId!,
        PRODUCT_APPLICABILITY_DISCOVER_WORKFLOW_TYPE
      ),
    enabled: Boolean(productId) && enabled,
    refetchInterval(queryState) {
      const status = queryState.state.data?.status
      if (
        status === 'failed' ||
        status === 'completed' ||
        status === 'cancelled' ||
        status === 'none'
      ) {
        return false
      }
      return PRODUCT_DISCOVER_WORKFLOW_POLL_MS
    },
    staleTime: 0,
  })

  useEffect(() => {
    const runId = query.data?.runId
    const status = query.data?.status
    if (
      productId == null ||
      runId == null ||
      status !== 'running'
    ) {
      streamingRunIdRef.current = null
      return
    }

    const abortController = new AbortController()
    const streamedEvents: WorkflowEvent[] = []
    void consumeWorkflowEventStream({
      runId,
      signal: abortController.signal,
      onEvent: (event) => {
        streamedEvents.push(event)
        streamingRunIdRef.current = runId
        queryClient.setQueryData<LatestWorkflowForEntityResponse>(
          [PRODUCTS_QUERY_KEY, productId, 'discover-workflow-latest'],
          (previous) => {
            if (previous == null || previous.runId !== runId) {
              return previous
            }
            return {
              ...previous,
              events: [...streamedEvents],
              status: discoverStatusFromEvents(
                streamedEvents,
                previous.status
              ),
              result: workflowResultFromEvents(
                streamedEvents,
                previous.result
              ),
            }
          }
        )
      },
    }).catch((error: unknown) => {
      if (abortController.signal.aborted) {
        return
      }
      streamingRunIdRef.current = null
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }
    })

    return () => {
      abortController.abort()
      if (streamingRunIdRef.current === runId) {
        streamingRunIdRef.current = null
      }
    }
  }, [productId, query.data?.runId, query.data?.status, queryClient])

  return query
}

function invalidateDiscoverDependentQueries(
  queryClient: QueryClient,
  productId: string
) {
  void queryClient.invalidateQueries({
    queryKey: [PRODUCTS_QUERY_KEY, productId, 'applicability'],
  })
  void queryClient.invalidateQueries({
    queryKey: [PRODUCT_MACHINERY_COMPLIANCE_QUERY_KEY, productId],
  })
  void queryClient.invalidateQueries({
    queryKey: [V2_PRODUCTS_QUERY_KEY],
  })
  void queryClient.invalidateQueries({
    queryKey: [OPEN_ROHS_PARTS_QUERY_KEY, productId],
  })
  void queryClient.invalidateQueries({
    queryKey: [PRODUCTS_QUERY_KEY, productId, 'discover-workflow-latest'],
  })
}

export function useDiscoverV2Product(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<{ runId: string }, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post<{ runId: string }>(
        `/v2/products/${productId}/discover`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId, 'discover-workflow-latest'],
      })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId, 'applicability'],
      })
    },
  })
}

export function useDiscoverV2ProductLaw(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<{ runId: string }, Error, ProductLawKey>({
    mutationFn: async (law) => {
      const response = await apiClient.post<{ runId: string }>(
        `/v2/products/${productId}/laws/${law}/discover`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId, 'discover-workflow-latest'],
      })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId, 'applicability'],
      })
    },
  })
}

export function useInvalidateOnDiscoverWorkflowTerminal(
  productId: string,
  workflow: LatestWorkflowForEntityResponse | undefined
) {
  const queryClient = useQueryClient()
  const previousStatusRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const status = workflow?.status
    const previousStatus = previousStatusRef.current
    previousStatusRef.current = status

    const transitionedFromRunning =
      previousStatus === 'running' &&
      (status === 'completed' ||
        status === 'failed' ||
        status === 'cancelled')

    if (transitionedFromRunning) {
      invalidateDiscoverDependentQueries(queryClient, productId)
    }
  }, [productId, queryClient, workflow?.status])
}

export function useGetProductLawQuestionnaire(
  productId: string,
  law: ProductLawKey,
  enabled: boolean
) {
  return useQuery<LawQuestionnaireView, Error>({
    queryKey: [PRODUCTS_QUERY_KEY, productId, 'applicability', law, 'questionnaire'],
    queryFn: async () => {
      const response = await apiClient.get<LawQuestionnaireView>(
        `/products/${productId}/applicability/${law}/questionnaire`
      )
      return response.data
    },
    enabled: enabled && productId.length > 0,
  })
}

export function useSetProductLawQuestionnaireAnswers(
  productId: string,
  law: ProductLawKey
) {
  const queryClient = useQueryClient()

  return useMutation<
    ProductLawApplicability,
    Error,
    SetProductLawQuestionnaireAnswersPayload
  >({
    mutationFn: async (payload) => {
      const response = await apiClient.put<ProductLawApplicability>(
        `/products/${productId}/applicability/${law}/answers`,
        payload
      )
      return response.data
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId, 'applicability'],
      })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId, 'applicability', law, 'questionnaire'],
      })
      void queryClient.invalidateQueries({
        queryKey: [V2_PRODUCTS_QUERY_KEY],
      })
      if (law === 'machinery' && data.status === 'APPLICABLE') {
        void queryClient.invalidateQueries({
          queryKey: [PRODUCT_MACHINERY_COMPLIANCE_QUERY_KEY, productId],
        })
      }
      if (law === 'rohs') {
        void queryClient.invalidateQueries({
          queryKey: [OPEN_ROHS_PARTS_QUERY_KEY, productId],
        })
      }
    },
  })
}

export function useSetProductLawApplicability(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<
    ProductLawApplicability,
    Error,
    SetProductLawApplicabilityPayload
  >({
    mutationFn: async ({ law, applicability }) => {
      const response = await apiClient.put<ProductLawApplicability>(
        `/products/${productId}/applicability/${law}`,
        applicability
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId, 'applicability'],
      })
      void queryClient.invalidateQueries({
        queryKey: [V2_PRODUCTS_QUERY_KEY],
      })
      if (
        variables.law === 'machinery' &&
        variables.applicability.status === 'APPLICABLE'
      ) {
        void queryClient.invalidateQueries({
          queryKey: [PRODUCT_MACHINERY_COMPLIANCE_QUERY_KEY, productId],
        })
      }
      if (variables.law === 'rohs') {
        void queryClient.invalidateQueries({
          queryKey: [OPEN_ROHS_PARTS_QUERY_KEY, productId],
        })
      }
    },
  })
}

export function useGenerateProductDescription(productId: string) {
  return useMutation<{ description: string }, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post<{ description: string }>(
        `/products/${productId}/description-generation`
      )
      return response.data
    },
  })
}

export function useUpdateProduct() {
  const queryClient = useQueryClient()

  return useMutation<LexProduct, Error, UpdateLexProductPayload>({
    mutationFn: async (payload: UpdateLexProductPayload) => {
      const { productId, name, description, yearOfManufacture, productModel } =
        payload
      const body: Record<string, unknown> = {}

      if (Object.prototype.hasOwnProperty.call(payload, 'name')) {
        body.name = name ?? null
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'description')) {
        body.description = description ?? null
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'yearOfManufacture')) {
        body.yearOfManufacture = yearOfManufacture ?? null
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'productModel')) {
        body.productModel =
          productModel != null && productModel.trim() !== ''
            ? productModel.trim()
            : null
      }

      const response = await apiClient.patch<LexProduct>(
        `/products/${productId}`,
        body
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, variables.productId],
      })
    },
  })
}

export function useDeleteProduct() {
  const queryClient = useQueryClient()

  return useMutation<DeleteLexProductResponse, Error, string>({
    mutationFn: async (productId: string) => {
      const response = await apiClient.delete<DeleteLexProductResponse>(
        `/products/${productId}`
      )
      return response.data
    },
    onSuccess: (_data, productId) => {
      void queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId],
      })
      void queryClient.invalidateQueries({ queryKey: [V2_PRODUCTS_QUERY_KEY] })
    },
  })
}

export function useUpdateProductInferredFact() {
  const queryClient = useQueryClient()

  return useMutation<
    ProductInferredFact,
    Error,
    UpdateProductInferredFactPayload
  >({
    mutationFn: async ({ productId, factId, label, value }) => {
      const response = await apiClient.patch<ProductInferredFact>(
        `/products/${productId}/inferred-facts/${factId}`,
        {
          label,
          value,
        }
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, variables.productId],
      })
    },
  })
}

export function useDeleteProductInferredFact() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, DeleteProductInferredFactPayload>({
    mutationFn: async ({ productId, factId }) => {
      await apiClient.delete(`/products/${productId}/inferred-facts/${factId}`)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, variables.productId],
      })
    },
  })
}

export function useProductRecommendations(
  productId: string,
  sessionId: string,
  statuses?: ProductRecommendationStatus[]
) {
  return useQuery<ProductRecommendation[], Error>({
    queryKey: productRecommendationsQueryKey(
      productId,
      sessionId,
      statuses ?? []
    ),
    queryFn: async () => {
      const response = await apiClient.get(
        `/products/${productId}/recommendations`,
        {
          params: {
            sessionId,
            ...(statuses !== undefined && statuses.length > 0
              ? { status: statuses }
              : {}),
          },
        }
      )
      return productRecommendationListResponseSchema.parse(response.data)
        .recommendations
    },
    enabled: Boolean(productId) && Boolean(sessionId),
  })
}

function productRecommendationsQueryKey(
  productId: string,
  sessionId: string,
  statuses: ProductRecommendationStatus[] = []
) {
  return [
    PRODUCTS_QUERY_KEY,
    productId,
    'recommendations',
    sessionId,
    statuses,
  ] as const
}

/**
 * Merges a server-pushed list of recommendations into the TanStack Query cache
 * without triggering a network request. Existing entries (by id) are preserved
 * so that any status updates from accept/dismiss mutations are not overwritten.
 *
 * Called from `onData` when a `data-lex-product-recommendations-sync` SSE event
 * arrives, before the corresponding tool-output-available event renders the card.
 */
export function setProductRecommendationsCache(
  queryClient: QueryClient,
  productId: string,
  sessionId: string,
  incoming: ProductRecommendation[]
): void {
  queryClient.setQueryData<ProductRecommendation[]>(
    productRecommendationsQueryKey(productId, sessionId),
    (previous) => {
      if (previous === undefined) {
        return incoming
      }
      const existingIds = new Set(previous.map((rec) => rec.id))
      const newOnes = incoming.filter((rec) => !existingIds.has(rec.id))
      if (newOnes.length === 0) {
        return previous
      }
      return [...previous, ...newOnes]
    }
  )
}

function patchAcceptedRecommendationInCache(
  queryClient: QueryClient,
  productId: string,
  recommendation: ProductRecommendation
): void {
  queryClient.setQueryData<ProductRecommendation[]>(
    productRecommendationsQueryKey(productId, recommendation.sessionId),
    (previous) => {
      if (previous === undefined) {
        return previous
      }
      return previous.map((item) =>
        item.id === recommendation.id ? recommendation : item
      )
    }
  )
}

/** Optimistic status update while chat resolve+continue stream runs. */
export function patchRecommendationStatusInCache(
  queryClient: QueryClient,
  productId: string,
  sessionId: string,
  recommendationId: string,
  status: ProductRecommendationStatus
): void {
  queryClient.setQueryData<ProductRecommendation[]>(
    productRecommendationsQueryKey(productId, sessionId),
    (previous) => {
      if (previous === undefined) {
        return previous
      }
      return previous.map((item) =>
        item.id === recommendationId ? { ...item, status } : item
      )
    }
  )
}

export function invalidateProductRecommendationQueries(
  queryClient: QueryClient,
  productId: string,
  sessionId?: string
): void {
  if (sessionId !== undefined) {
    void queryClient.invalidateQueries({
      queryKey: [PRODUCTS_QUERY_KEY, productId, 'recommendations', sessionId],
    })
    void queryClient.invalidateQueries({
      queryKey: llmChatSessionQueryKey(sessionId),
    })
  } else {
    void queryClient.invalidateQueries({
      queryKey: [PRODUCTS_QUERY_KEY, productId, 'recommendations'],
    })
  }
  void queryClient.invalidateQueries({
    queryKey: [PRODUCTS_QUERY_KEY, productId],
  })
  void queryClient.invalidateQueries({
    queryKey: [PRODUCTS_QUERY_KEY],
  })
  void queryClient.invalidateQueries({
    queryKey: llmChatSessionsListQueryKey(),
  })
}

export function useAcceptProductRecommendation() {
  const queryClient = useQueryClient()

  return useMutation<
    {
      applied: boolean
      recommendation: ProductRecommendation
      chatContinuationEligible: boolean
    },
    Error,
    ResolveProductRecommendationPayload
  >({
    mutationFn: async ({ productId, recommendationId }) => {
      const response = await apiClient.post(
        `/products/${productId}/recommendations/${recommendationId}/accept`
      )
      return productRecommendationAcceptResponseSchema.parse(response.data)
    },
    onSuccess: ({ applied, recommendation }, variables) => {
      if (applied) {
        patchAcceptedRecommendationInCache(
          queryClient,
          variables.productId,
          recommendation
        )
      }
      invalidateProductRecommendationQueries(
        queryClient,
        variables.productId,
        recommendation.sessionId
      )
      if (applied) {
        toast.success(
          i18n.t('recommendations.feedback.saved', { ns: 'llmChat' })
        )
        return
      }
      if (recommendation.status === 'stale') {
        toast.warning(
          i18n.t('recommendations.feedback.stale', { ns: 'llmChat' })
        )
      }
    },
    onError: (_error, variables) => {
      toast.error(
        i18n.t('recommendations.errors.actionFailed', { ns: 'llmChat' })
      )
      invalidateProductRecommendationQueries(queryClient, variables.productId)
    },
  })
}

export function useDismissProductRecommendation() {
  const queryClient = useQueryClient()

  return useMutation<
    {
      recommendation: ProductRecommendation
      chatContinuationEligible: boolean
    },
    Error,
    ResolveProductRecommendationPayload
  >({
    mutationFn: async ({ productId, recommendationId }) => {
      const response = await apiClient.post(
        `/products/${productId}/recommendations/${recommendationId}/dismiss`
      )
      return productRecommendationMutationResponseSchema.parse(response.data)
    },
    onSuccess: ({ recommendation }, variables) => {
      invalidateProductRecommendationQueries(
        queryClient,
        variables.productId,
        recommendation.sessionId
      )
    },
    onError: (_error, variables) => {
      toast.error(
        i18n.t('recommendations.errors.actionFailed', { ns: 'llmChat' })
      )
      invalidateProductRecommendationQueries(queryClient, variables.productId)
    },
  })
}

export function useGetProduct(productId: string) {
  return useQuery<LexProduct, Error>({
    queryKey: [PRODUCTS_QUERY_KEY, productId],
    queryFn: async () => {
      const response = await apiClient.get<LexProduct>(`/products/${productId}`)
      return response.data
    },
    enabled: Boolean(productId),
    refetchInterval(query) {
      const data = query.state.data
      if (!data) {
        return false
      }
      if (!data.isInitialized) {
        return 2000
      }
      return false
    },
  })
}

export function useGetProductMachineryDirectiveCompliance(
  productId: string,
  enabled = true
) {
  return useQuery<MachineryDirectiveCompliance | null, Error>({
    queryKey: [PRODUCTS_QUERY_KEY, productId, 'machinery-directive'],
    queryFn: async () => {
      try {
        const response = await apiClient.get<MachineryDirectiveCompliance>(
          `/products/${productId}/machinery-directive-compliance`
        )
        return response.data
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled,
  })
}

export function useCreateProductMachineryDirectiveCompliance(
  productId: string
) {
  const queryClient = useQueryClient()

  return useMutation<
    MachineryDirectiveCompliance,
    Error,
    { contentLanguage: SupportedLanguage }
  >({
    mutationFn: async (payload) => {
      const response = await apiClient.post<MachineryDirectiveCompliance>(
        `/products/${productId}/machinery-directive-compliance`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId, 'machinery-directive'],
      })
    },
  })
}

export function useGetProductComplianceStudios(
  productId: string,
  enabled = true
) {
  const {
    data: machineryDirectiveCompliance,
    isLoading: isLoadingMachineryDirective,
    isError: isErrorMachineryDirective,
    refetch: refetchMachineryDirective,
  } = useGetProductMachineryDirectiveCompliance(productId, enabled)

  return {
    machineryDirectiveCompliance,
    isLoading: isLoadingMachineryDirective,
    isError: isErrorMachineryDirective,
    isLoadingMachineryDirective,
    isErrorMachineryDirective,
    refetchMachineryDirective,
  }
}

/**
 * Latest persisted `product_init` workflow for polling step / LLM events on the UI.
 * Poll while mounted — do not stop when a run hits `completed` early (e.g. batch skip);
 * initialization panel unmounts once `product.isInitialized` is true.
 */
export function useProductInitWorkflowLatest(productId: string | undefined) {
  return useQuery<LatestWorkflowForEntityResponse, Error>({
    queryKey: [PRODUCTS_QUERY_KEY, productId, 'product-init-workflow-latest'],
    queryFn: async () =>
      getLatestWorkflowForEntity(
        'product',
        productId!,
        PRODUCT_INIT_WORKFLOW_TYPE
      ),
    enabled: Boolean(productId),
    refetchInterval(query) {
      const status = query.state.data?.status
      if (status === 'failed') {
        return false
      }
      return 2000
    },
    staleTime: 0,
  })
}

/**
 * Latest persisted `product_metadata_refresh` workflow for Description refresh UX.
 * Poll while the detail page is mounted and the product is initialized — do not
 * stop on `completed` or `failed` (a later Source add may start a new run).
 */
export function useProductMetadataRefreshWorkflowLatest(
  productId: string | undefined,
  enabled: boolean
) {
  return useQuery<LatestWorkflowForEntityResponse, Error>({
    queryKey: [
      PRODUCTS_QUERY_KEY,
      productId,
      'product-metadata-refresh-workflow-latest',
    ],
    queryFn: async () =>
      getLatestWorkflowForEntity(
        'product',
        productId!,
        PRODUCT_METADATA_REFRESH_WORKFLOW_TYPE
      ),
    enabled: Boolean(productId) && enabled,
    refetchInterval: 2000,
    staleTime: 0,
  })
}

export function useRetryProductInitialization(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<{ accepted: boolean }, Error>({
    mutationFn: async () => {
      const response = await apiClient.post<{ accepted: boolean }>(
        `/products/${productId}/initialize`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          PRODUCTS_QUERY_KEY,
          productId,
          'product-init-workflow-latest',
        ],
      })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId],
      })
    },
  })
}
